<script setup lang="ts">
import { computed } from "vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import {
  type IPromoProduct,
  IPromoProductProgressiveDiscountType,
  type IPromoProductProgressiveDiscountValue,
} from "@magnit/unit-catalog/src/unit-catalog.types";
import BonusIcon from "@magnit/icons/src/assets/icons/loyalty-bonus-16-color.svg";
import MagnetIcon from "@magnit/icons/src/assets/icons/loyalty-magnitik-16-color.svg";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import ActionDate from "./ActionDate.vue";
import ProductDetailsPrice from "./Price.vue";
import type { IProductDetailsOfferProps } from "../types/product-details";
import useProduct from "../composables/useProduct";
import ProductDetailsProgressiveDiscount from "@magnit/unit-product-details/src/components/ProgressiveDiscount.vue";
import type { AnalyticsEventArguments } from "@magnit/unit-product-details/src/types/analytics";

const props = defineProps<IProductDetailsOfferProps>();

const displayedProgressive =
  defineModel<IPromoProductProgressiveDiscountValue | null>(
    "displayedProgressive",
  );

const emit = defineEmits<{
  analytics: AnalyticsEventArguments;
}>();

const { productName, productPrice, showNotInStock } = useProduct(props.product);

const withProgressive = computed(() =>
  Boolean(
    "progressiveDiscount" in props.product &&
      props.product.progressiveDiscount?.length,
  ),
);

const promoProduct = computed(
  () => (withProgressive.value && (props.product as IPromoProduct)) || null,
);

const progressiveStringValues = computed(() => {
  if (!displayedProgressive.value) return null;
  switch (promoProduct.value && promoProduct.value.progressiveDiscountType) {
    case IPromoProductProgressiveDiscountType.BONUSES:
      return {
        start: `+ ${displayedProgressive.value?.value}`,
        end: "бонусами",
        icon: BonusIcon,
      };
    case IPromoProductProgressiveDiscountType.MAGNETS:
      return {
        start: `+ ${displayedProgressive.value?.value}`,
        end: "Магнитиками",
        icon: MagnetIcon,
      };
    case IPromoProductProgressiveDiscountType.CASHBACK:
      return {
        start: `+ ${displayedProgressive.value?.value}%`,
        end: "бонусами",
        icon: BonusIcon,
      };
    default:
      return null;
  }
});

const progressivePrice = computed(
  () =>
    (promoProduct.value &&
      promoProduct.value.progressiveDiscountType ===
        IPromoProductProgressiveDiscountType.PRICE &&
      displayedProgressive.value?.value) ||
    undefined,
);

const isProgressiveDiscountVisible = computed(
  () => withProgressive.value && promoProduct.value,
);
</script>

<template>
  <section
    class="product-details-offer"
    itemprop="offers"
    itemscope
    itemtype="http://schema.org/Offer"
  >
    <meta
      v-if="productPrice"
      itemprop="price"
      :content="String(productPrice).replace(',', '.')"
    />
    <meta itemprop="priceCurrency" content="RUB" />
    <meta
      v-if="'isForAdults' in product && product.isForAdults"
      itemprop="hasAdultConsideration"
    />
    <meta
      v-if="'alcohol' in product && product.alcohol"
      itemprop="hasAdultConsideration"
    />
    <ActionDate
      v-if="'endDate' in product && product.endDate"
      class="product-details-offer__action-date"
      :end="product.endDate"
    />
    <VHidden :hide="{ xs: false, l: true }">
      <VText
        class="product-details-offer__title"
        font="title-large"
        itemprop="name"
      >
        {{ productName }}
      </VText>
      <VText
        v-if="showNotInStock"
        tag="div"
        class="product-details-offer__not-in-stock"
        font="body-large-accent"
        color="accent"
      >
        Нет в наличии
      </VText>
    </VHidden>
    <ProductDetailsPrice
      :product="product"
      :progressive-price="progressivePrice"
      :progressive-discount-label="progressiveDiscount"
    />
    <div
      v-if="progressiveStringValues"
      class="product-details-offer__progressive-string"
    >
      <VText font="body-large-accent">
        {{ progressiveStringValues.start }}
      </VText>
      <VIcon><Component :is="progressiveStringValues.icon" /></VIcon>
      <VText font="body-small-regular">
        {{ progressiveStringValues.end }}
      </VText>
    </div>
    <VHidden :hide="{ xs: true, l: false }">
      <ProductDetailsProgressiveDiscount
        v-if="isProgressiveDiscountVisible"
        v-model="displayedProgressive"
        :product="props.product"
        :variants="promoProduct?.progressiveDiscount"
        :type="promoProduct?.progressiveDiscountType"
        :category-name="props.categoryName"
        no-caption
        @analytics="(...args) => emit('analytics', ...args)"
      />
    </VHidden>
    <div class="product-details-offer__reviews">
      <slot name="reviews"> </slot>
    </div>
    <slot name="ecom" />
  </section>
</template>

<style lang="postcss" scoped>
.product-details-offer {
  display: flex;
  flex-direction: column;

  @media (--pl-viewport-from-l) {
    margin-bottom: var(--pl-unit-x4);
    padding: var(--pl-unit-x4);
    border: 1px solid var(--pl-border-secondary-default);
    border-radius: var(--pl-unit-x4);
  }

  &__action-date {
    margin-bottom: var(--pl-unit-x3);

    @media (--pl-viewport-from-l) {
      margin-bottom: var(--pl-unit-x2);
    }
  }

  &__title {
    display: inline-block;
    margin-bottom: var(--pl-unit-x2);
  }

  &__not-in-stock {
    margin-bottom: var(--pl-unit-x2);
  }

  &__progressive-string {
    display: inline-flex;
    align-items: center;
    padding-top: 2px;
    gap: var(--pl-unit-x1);
  }
}
</style>
